import { type Connection, PublicKey, SYSVAR_CLOCK_PUBKEY } from '@solana/web3.js';
import { deriveEventScheduleAddress } from '../builders/event-schedule.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { decodeChainClock } from './chain-clock.js';
import { fetchMarketConfig } from './fetch.js';
import type {
  AdminConfigAccount,
  EventScheduleAccount,
  TimedMarketConfigAccount,
} from './types.js';

export interface TimedCreationContext {
  readonly eventSchedule: PublicKey;
  readonly marketConfig: PublicKey;
  readonly quoteMint: PublicKey;
  readonly config: TimedMarketConfigAccount;
  readonly start: bigint;
  readonly end: bigint;
  readonly now: bigint;
  readonly restricted: boolean;
}

/** Read authoritative binding and clock; admission is separate so landed markets can recover. */
export async function fetchTimedCreationContext(
  connection: Connection,
  eventSchedule: PublicKey,
): Promise<TimedCreationContext> {
  const program = await getProgram(connection);
  const adminAddress = PublicKey.findProgramAddressSync(
    [Buffer.from('admin_config')],
    MORFI_OPINION_PROGRAM_ID,
  )[0];
  const [eventInfo, adminInfo, clockInfo] = await connection.getMultipleAccountsInfo(
    [eventSchedule, adminAddress, SYSVAR_CLOCK_PUBKEY],
    'confirmed',
  );
  if (!eventInfo?.owner.equals(program.programId))
    throw new Error('Event schedule is missing or belongs to a different network/program.');
  if (!adminInfo?.owner.equals(program.programId) || !clockInfo || clockInfo.data.length < 40)
    throw new Error('Timed creation authority or chain clock is unavailable.');
  const schedule = program.coder.accounts.decode<EventScheduleAccount>(
    'eventSchedule',
    eventInfo.data,
  );
  if (!deriveEventScheduleAddress(BigInt(schedule.scheduleNonce.toString())).equals(eventSchedule))
    throw new Error('Event schedule is not canonical.');
  const start = BigInt(schedule.tradingStartedTs.toString());
  const end = BigInt(schedule.tradingEndTs.toString());
  if (end <= start || end > BigInt(schedule.maxTradingEndTs.toString()))
    throw new Error('Event schedule has an invalid trading window.');
  const config = await fetchMarketConfig(connection, schedule.marketConfig);
  if ('convictionResolutionThresholdPpb' in config)
    throw new Error('The event requires a timed market configuration.');
  const admin = program.coder.accounts.decode<AdminConfigAccount>('adminConfig', adminInfo.data);
  return {
    eventSchedule,
    marketConfig: schedule.marketConfig,
    quoteMint: config.common.quoteMint,
    config,
    start,
    end,
    now: decodeChainClock(clockInfo).unixTimestampSeconds,
    restricted: admin.timedMarketCreationRestricted,
  };
}

export function assertTimedCreationAllowed(context: TimedCreationContext): void {
  if (!context.config.common.enabled)
    throw new Error('The timed market configuration is disabled.');
  if (context.restricted)
    throw new Error(
      'Timed market creation is restricted. Ordinary-user approval is not supported.',
    );
  if (context.start < context.now + 60n)
    throw new Error('The event must start at least 60 seconds after market creation.');
}
